import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '../context/adminAuth';
import { message, Spin } from 'antd';
import { useEffect, useState } from 'react';
import supabase from '../config/supabaseConfig';

interface AdminRouteProps {
    children: React.ReactNode;
}

export const AdminRoute = ({ children }: AdminRouteProps) => {
    const { admin, isLoading } = useAdminAuth();
    const [isApprovedAdmin, setIsApprovedAdmin] = useState<boolean | null>(null);
    const [isChecking, setIsChecking] = useState(true);

    const adminId = admin?.id;

    // Check if the user is an approved admin.
    // Waits for the session to finish loading: checking while it was still loading
    // recorded "not an admin" and could redirect a real admin to /login-admin before
    // the actual check ran. Keyed on the id (not the user object) so the hourly token
    // refresh doesn't re-check and flash the loader over the dashboard.
    useEffect(() => {
        if (isLoading) return;

        let cancelled = false;

        const checkIfApprovedAdmin = async () => {
            if (!adminId) {
                setIsApprovedAdmin(false);
                setIsChecking(false);
                return;
            }

            setIsChecking(true);
            try {
                // Query the admins table
                const { data, error } = await supabase
                    .from('admins')
                    .select('approved')
                    .eq('id', adminId)
                    .maybeSingle();

                if (cancelled) return;
                setIsApprovedAdmin(!error && data?.approved === true);
            } catch {
                if (cancelled) return;
                message.error("Impossible de vérifier votre statut d'administrateur");
                setIsApprovedAdmin(false);
            } finally {
                if (!cancelled) setIsChecking(false);
            }
        };

        checkIfApprovedAdmin();
        return () => {
            cancelled = true;
        };
    }, [adminId, isLoading]);

    // Show loading state
    if (isLoading || isChecking) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Spin size="large" tip="Vérification de votre session d'administrateur..." />
            </div>
        );
    }

    // Redirect if not authenticated
    if (!admin) {
        return <Navigate to="/login-admin" />;
    }

    // Redirect if authenticated but not an approved admin
    if (!isApprovedAdmin) {
        return <Navigate to="/login-admin" />;
    }
    return <>{children}</>;
};